/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo, useState } from 'react'

import { Link } from 'react-router-dom'

import { Alert, Badge, PageContainer, PageHeader } from '@/components/ui'
import { TOTAL_INSIGHTS } from '@/insights/insightCatalogue'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'
import { LoadingPanel } from '../LoadingPanel'
import { APP_GRANTS, APP_GRANT_LABELS } from '../permissions'
import { CustomerDropdown } from '../permissions/CustomerDropdown'
import { useAdminAssetGroups } from '../permissions/useAdminAssetGroups'
import type { AdminAssetGroup } from '../types'

/**
 * Every asset group (sub-company) with its assets, users and the apps and insights its
 * users actually get (MEGA-776, MEGA-781). A group's profile replaces its company's
 * lists wholesale, so "Overridden" says whose lists the effective ones are; without a
 * profile they are the company's.
 */
const COLUMNS: AdminColumns<AdminAssetGroup> = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (group) => group.name,
    cell: ({ row }) => (
      <Link className="link link-primary" to={`/admin/asset-groups/${String(row.original.companyId)}`}>
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'company',
    header: 'Company',
    accessorFn: (group) => group.customerName,
    meta: { className: 'whitespace-nowrap' },
    cell: ({ row }) => (
      <Link className="link link-hover" to={`/admin/companies/${String(row.original.customerId)}`}>
        {row.original.customerName}
      </Link>
    ),
  },
  { id: 'assets', header: 'Assets', accessorFn: (group) => group.assetCount },
  { id: 'users', header: 'Users', accessorFn: (group) => group.userCount },
  {
    id: 'overridden',
    header: 'Overridden',
    accessorFn: (group) => group.overridden,
    meta: { tdProps: (group: AdminAssetGroup) => ({ 'data-testid': `overridden-${String(group.companyId)}` }) },
    cell: ({ row }) =>
      row.original.overridden ? (
        <Badge variant="info" size="xs" title="Its own profile replaces the company’s apps and insights">
          Yes
        </Badge>
      ) : (
        <span className="text-base-content/60" title="Its users get the company’s apps and insights">
          No
        </span>
      ),
  },
  {
    id: 'insights',
    header: 'Insights',
    // Uncapped groups have no count, so they sort last.
    accessorFn: (group) => group.effective?.insights.length,
    meta: {
      className: 'whitespace-nowrap',
      tdProps: (group: AdminAssetGroup) => ({ 'data-testid': `insights-${String(group.companyId)}` }),
    },
    cell: ({ row }) =>
      row.original.effective ? (
        `${String(row.original.effective.insights.length)}/${String(TOTAL_INSIGHTS)}`
      ) : (
        <Badge size="xs" title="No profile saved for the group or its company — users keep their own permissions">
          no profile
        </Badge>
      ),
  },
  ...APP_GRANTS.map((grant): AdminColumns<AdminAssetGroup>[number] => ({
    id: grant,
    header: APP_GRANT_LABELS[grant],
    accessorFn: (group) => group.effective?.grants.includes(grant) ?? false,
    meta: { tdProps: (group: AdminAssetGroup) => ({ 'data-testid': `grant-${String(group.companyId)}-${grant}` }) },
    cell: ({ row }) => {
      const { effective } = row.original
      return effective?.grants.includes(grant) ? (
        <Badge variant="success" size="xs">
          On
        </Badge>
      ) : (
        <span
          className="text-base-content/40"
          aria-label={effective ? 'disabled' : 'not configured'}
          title={effective ? undefined : 'No profile saved'}
        >
          —
        </span>
      )
    },
  })),
]

const AdminAssetGroupsPage: React.FC = () => {
  const { groups, isLoading, error } = useAdminAssetGroups()
  const [filter, setFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState<number>()

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return groups.filter(
      (group) =>
        (companyFilter === undefined || group.customerId === companyFilter) &&
        (!q ||
          group.name.toLowerCase().includes(q) ||
          group.customerName.toLowerCase().includes(q) ||
          String(group.companyId).includes(q))
    )
  }, [groups, filter, companyFilter])

  const table = useAdminTable(visible, COLUMNS)
  const columnCount = COLUMNS.length

  return (
    <PageContainer>
      <Link to="/admin" className="link link-hover mb-2 inline-block text-sm">
        ← Admin
      </Link>
      <PageHeader
        title="Asset Groups"
        subtitle={
          isLoading ? undefined : `${String(groups.length)} ${groups.length === 1 ? 'asset group' : 'asset groups'}`
        }
      />

      {error && <Alert message={error} type="error" />}

      <div className="mb-3 flex items-end gap-3">
        <input
          className="input input-bordered input-sm w-full max-w-md"
          placeholder="Filter by name, company or id"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter asset groups"
        />
        <div className="w-64">
          <CustomerDropdown value={companyFilter} onChange={setCompanyFilter} />
        </div>
        {companyFilter !== undefined && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCompanyFilter(undefined)}>
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <LoadingPanel label="Loading asset groups…" />
      ) : (
        <div className="overflow-x-auto rounded-lg bg-base-100 shadow">
          <table className="table table-sm table-zebra">
            <thead>
              <tr>
                <AdminHeaderCells table={table} />
              </tr>
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.original.companyId}>
                  <AdminRowCells row={row} />
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="p-4 text-center italic text-base-content/60">
                    No asset groups match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  )
}

export default AdminAssetGroupsPage
